import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { logException } from '../../services/utils';
import find from 'lodash/find';
import get from 'lodash/get';
import moment from 'moment-timezone';
import FormattedAddress from '../FormattedAddress';
import Avatar from '../Avatar';
import DashboardBreadcrumb from '../DashboardBreadcrumb';
import ModalClientDetails from '../ModalClientDetails';
import ModalEnrollClient from '../ModalEnrollClient';
import ZeroStatePrompt from '../ZeroStatePrompt';
import GOOGLE_MAPS_THEME from '../../constants/googleMapsTheme';
import * as analytics from '../../services/analytics';
import './ClientDetailsView.css';

const propTypes = {
  client: PropTypes.object,
};

class ClientDetailsView extends PureComponent {
  componentDidMount() {
    analytics.page('Broker Portal - Client Details');
    const mapInterval = setInterval(() => {
      if (window.google && window.google.maps) {
        window.clearInterval(mapInterval);
        this.initMap();
      }
    }, 500);

    this.props.getClient(this.props.match.params.clientId).then(() => {
      this.props.getQuotes({ clientId: this.props.client.id });
      this.props.getPolicies({ clientId: this.props.client.id });
    });
  }

  componentWillUnmount() {
    this.enrollClientModal.hide();
    this.updateClientModal.hide();
  }

  initMap = () => {
    const { line1, city, region, postal } =
      get(this.props.client, 'person.address') || {};
    if (city && region && postal) {
      const addressStr = [line1, city, region.name, postal].join(' ');
      const google = window.google;
      const mapOptions = {
        zoom: 11,
        draggable: false,
        disableDefaultUI: true,
        draggableCursor: 'default',
        styles: GOOGLE_MAPS_THEME,
      };

      const mapElement = document.getElementById('clientMap');
      const map = new google.maps.Map(mapElement, mapOptions);
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: addressStr }, (results, status) => {
        if (status === 'OK') {
          map.setCenter(results[0].geometry.location);
          new google.maps.Marker({
            map: map,
            position: results[0].geometry.location,
            icon: '/img/map-marker.png',
          });
        } else {
          logException(
            'Geocode was not successful for the following reason: ' + status
          );
        }
      });
    }
  };

  getBadgeForStatus(status) {
    switch (status) {
      case 'inforce':
        return (
          <p>
            <span className="badge badge-success text-uppercase">In force</span>
          </p>
        );
      case 'canceled':
        return (
          <p>
            <span className="badge badge-danger text-uppercase">Canceled</span>
          </p>
        );
      default:
        return <p>n/a</p>;
    }
  }

  getProductName(verticalCode) {
    switch (verticalCode) {
      case 'chubb_passport360':
        return 'Passport 360';
      case 'chubb_boa':
        return 'BOA';
      default:
        return 'n/a';
    }
  }

  getQuoteProposalLink(quote) {
    const doc = find(quote.documents, { document_type: 'quote_proposal' });
    if (doc) {
      return <a href={doc.url}>View</a>;
    }
  }

  handleClickPolicy = (e) => {
    const policyId = e.currentTarget.dataset.policyId;
    const { history, match } = this.props;
    history.push(`${match.url}/policies/${policyId}`);
  };

  handleEnrollClient = ({ product, method }) => {
    const {
      client,
      createQuote,
      handoffQuote,
      history,
      match,
      onNotifyError,
      onNotifySuccess,
    } = this.props;
    const clientFullName = `${client.person.first_name} ${client.person.last_name}`;
    const createQuotePromise = createQuote({
      clientId: client.id,
      verticalCode: product,
    });

    analytics.track('Broker Portal - Enroll Client Submitted', {
      method,
      product,
    });
    if (method === 'email') {
      createQuotePromise
        .then((quote) => {
          return handoffQuote({
            quoteNumber: quote.quote_number,
            handoffType: 'blank',
          });
        })
        .then(() => {
          onNotifySuccess(
            `<strong>${clientFullName}</strong> has been sent an enrollment email.`
          );
          this.enrollClientModal.hide();
        })
        .catch(onNotifyError);
    } else {
      createQuotePromise
        .then((quote) => {
          this.enrollClientModal.hide();
          switch (quote.vertical_code) {
            case 'chubb_boa':
              return history.push(
                `${match.url}/boa/quotes/${quote.quote_number}`
              );
            case 'chubb_passport360':
            default:
              return history.push(
                `${match.url}/passport360/quotes/${quote.quote_number}`
              );
          }
        })
        .catch(onNotifyError);
    }
  };

  handleUpdateClient = (clientDetails) => {
    const { updateClient, onNotifySuccess, onNotifyError } = this.props;
    analytics.track('Broker Portal - Update Client Submitted');
    updateClient(clientDetails)
      .then(() => {
        analytics.track('Broker Portal - Update Client Succeeded', {
          noninteraction: 1,
        });
        const clientFullName = `${clientDetails.person.first_name} ${clientDetails.person.last_name}`;
        const companyName = clientDetails.person.company_name;
        onNotifySuccess(
          `<strong>${
            clientDetails.client_type === 'business'
              ? companyName
              : clientFullName
          }</strong> has been updated successfully.`
        );
        this.updateClientModal.hide();
      })
      .catch((error) => {
        analytics.track('Broker Portal - Update Client Failed', {
          noninteraction: 1,
        });
        onNotifyError(error);
      });
  };

  showEnrollModal = (e) => {
    analytics.track('Broker Portal - Enroll Client Modal Opened', {
      value: parseInt(e.target.dataset.position, 10),
    });
    this.enrollClientModal.show();
  };

  showUpdateClientModal = () => {
    analytics.track('Broker Portal - Edit Client Modal Opened');
    this.updateClientModal.show();
  };

  handleSubmit = (e) => {
    e.preventDefault();
  };

  renderAvatar = (client) => {
    switch (client.client_type) {
      case 'individual':
        const fullName = `${get(client, 'person.first_name')} ${get(
          client,
          'person.last_name'
        )}`;
        return (
          <>
            <Avatar
              name={fullName}
              className="m-r-10"
              bgColor="#626262"
              size={60}
            />
            <div className="ClientDetailsView-header-client-info">
              <h2 className="semi-bold m-b-0">{fullName}</h2>
              <p>{client.person.email}</p>
            </div>
          </>
        );
      case 'business':
        return (
          <>
            <Avatar
              name={client.person.company_name}
              isBusiness
              className="m-r-10"
              bgColor="#626262"
              size={60}
            />
            <div className="ClientDetailsView-header-client-info">
              <h2 className="semi-bold m-b-0">{client.person.company_name}</h2>
              <p>{client.person.email}</p>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  render() {
    const {
      client,
      quotes,
      policies,
      isFetchingPolicies,
      isFetchingQuotes,
      isUpdatingClient,
    } = this.props;
    return (
      <Fragment>
        {client && (
          <div className="ClientDetailsView">
            <DashboardBreadcrumb client={client} />
            <div className="ClientDetailsView-header">
              <div className="ClientDetailsView-header-map">
                <div id="clientMap" />
              </div>
              <div className="ClientDetailsView-header-client">
                {this.renderAvatar(client)}
              </div>
            </div>
            <div className="container-fluid p-t-30">
              <div className="row">
                <div className="col-md-8">
                  {quotes.length || policies.length ? (
                    <div className="ClientDetailsView-policiesCard card">
                      <div className="ClientDetailsView-tableHeader card-header">
                        <div className="row justify-content-between align-items-center p-l-15 p-r-15">
                          <h2 className="ClientDetailsView-tableHeader-heading">
                            Policies
                          </h2>
                          <button
                            className="btn btn-complete btn-cons"
                            data-position="1"
                            onClick={this.showEnrollModal}
                          >
                            <i className="far fa-plus" /> Create offer
                          </button>
                        </div>
                      </div>
                      <div className="card-block no-padding">
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead>
                              <tr>
                                <th
                                  style={{ width: '16%' }}
                                  className="bold text-black text-uppercase"
                                >
                                  Product
                                </th>
                                <th
                                  style={{ width: '16%' }}
                                  className="bold text-black text-uppercase"
                                >
                                  Policy number
                                </th>
                                <th
                                  style={{ width: '16%' }}
                                  className="bold text-black text-uppercase"
                                >
                                  Issued on
                                </th>
                                <th
                                  style={{ width: '16%' }}
                                  className="bold text-black text-uppercase"
                                >
                                  Start date
                                </th>
                                <th
                                  style={{ width: '16%' }}
                                  className="bold text-black text-uppercase"
                                >
                                  End date
                                </th>
                                <th
                                  style={{ width: '10%' }}
                                  className="bold text-black text-uppercase"
                                >
                                  Status
                                </th>
                                <th style={{ width: '10%' }} />
                              </tr>
                            </thead>
                            <tbody>
                              {policies.map((policy) => (
                                <tr
                                  key={policy.id}
                                  className="ClientDetailsView-tableRow is-hoverable"
                                  data-policy-id={policy.id}
                                  onClick={this.handleClickPolicy}
                                >
                                  <td className="v-align-middle">
                                    <p>{policy.vertical.name}</p>
                                  </td>
                                  <td className="v-align-middle">
                                    <p>{policy.policy_number || 'n/a'}</p>
                                  </td>
                                  <td className="v-align-middle">
                                    <p>
                                      {moment(policy.created_at).format(
                                        'MMM DD, YYYY'
                                      )}
                                    </p>
                                  </td>
                                  <td className="v-align-middle">
                                    <p>
                                      {moment(policy.starts_at).format(
                                        'MMM DD, YYYY'
                                      )}
                                    </p>
                                  </td>
                                  <td className="v-align-middle">
                                    <p>
                                      {moment(policy.ends_at).format(
                                        'MMM DD, YYYY'
                                      )}
                                    </p>
                                  </td>
                                  <td className="v-align-middle">
                                    {this.getBadgeForStatus(policy.status)}
                                  </td>
                                </tr>
                              ))}
                              {quotes.map((quote) => (
                                <tr
                                  key={quote.quote_number}
                                  className="ClientDetailsView-tableRow"
                                  data-quote-number={quote.quote_number}
                                  onClick={this.handleClickQuote}
                                >
                                  <td className="v-align-middle">
                                    <p>
                                      {this.getProductName(quote.vertical_code)}
                                    </p>
                                  </td>
                                  <td className="v-align-middle">
                                    <p>n/a</p>
                                  </td>
                                  <td className="v-align-middle">
                                    <p>n/a</p>
                                  </td>
                                  <td className="v-align-middle">
                                    <p>n/a</p>
                                  </td>
                                  <td className="v-align-middle">
                                    <p>n/a</p>
                                  </td>
                                  <td className="v-align-middle">
                                    <p>
                                      <span className="badge badge-primary text-uppercase">
                                        Sent
                                      </span>
                                    </p>
                                  </td>
                                  <td className="v-align-middle">
                                    {this.getQuoteProposalLink(quote)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  ) : (
                    !isFetchingPolicies &&
                    !isFetchingQuotes && (
                      <div className="ClientDetailsView-policiesZeroCard card">
                        <ZeroStatePrompt
                          heading="This client has no policies."
                          icon="fa-file-alt"
                          promptText="Click create offer to begin"
                          btnText="Create offer"
                          data-position="0"
                          onClick={this.showEnrollModal}
                        />
                      </div>
                    )
                  )}
                </div>
                <div className="col-md-4">
                  <div className="ClientDetailsView-detailsCard card">
                    <h3 className="ClientDetailsView-detailsCard-header card-header">
                      <span>Details</span>
                      <span
                        className="ClientDetailsView-detailsCard-header-editLink card-link text-complete"
                        onClick={this.showUpdateClientModal}
                      >
                        Edit
                      </span>
                    </h3>
                    {client.person.company_name && (
                      <div className="ClientDetailsView-detailsCard-row row">
                        <div className="col-md-6">Company</div>
                        <div className="ClientDetailsView-detailsCard-detail col-md-6">
                          {client.person.company_name}
                        </div>
                      </div>
                    )}
                    <div className="ClientDetailsView-detailsCard-row row">
                      <div className="col-md-6">First name</div>
                      <div className="ClientDetailsView-detailsCard-detail col-md-6">
                        {client.person.first_name}
                      </div>
                    </div>
                    <div className="ClientDetailsView-detailsCard-row row">
                      <div className="col-md-6">Last name</div>
                      <div className="ClientDetailsView-detailsCard-detail col-md-6">
                        {client.person.last_name}
                      </div>
                    </div>
                    <div className="ClientDetailsView-detailsCard-row row">
                      <div className="col-md-6">Email</div>
                      <div className="ClientDetailsView-detailsCard-detail col-md-6">
                        {client.person.email}
                      </div>
                    </div>
                    <div className="ClientDetailsView-detailsCard-row row">
                      <div className="col-md-6">Date of Birth</div>
                      <div className="ClientDetailsView-detailsCard-detail col-md-6">
                        {(client.person.dob &&
                          moment(client.person.dob).format('MMM DD, YYYY')) ||
                          'N/A'}
                      </div>
                    </div>
                    <div className="ClientDetailsView-detailsCard-row row">
                      <div className="col-md-6">Phone</div>
                      <div className="ClientDetailsView-detailsCard-detail col-md-6">
                        {client.person.phone_number}
                      </div>
                    </div>
                    {client.person.address && (
                      <div className="ClientDetailsView-detailsCard-row row">
                        <div className="col-md-6">Address</div>
                        <div className="ClientDetailsView-detailsCard-detail col-md-6">
                          <FormattedAddress address={client.person.address} />
                        </div>
                      </div>
                    )}
                    <div className="ClientDetailsView-detailsCard-row row">
                      <div className="col-md-6">Added on</div>
                      <div className="ClientDetailsView-detailsCard-detail col-md-6">
                        {moment(client.created_at).format('MMM DD, YYYY')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <ModalClientDetails
              isUpdatingClient={isUpdatingClient}
              client={client}
              onSubmit={this.handleUpdateClient}
              ref={(c) => (this.updateClientModal = c)}
            />
            <ModalEnrollClient
              client={client}
              onSubmit={this.handleEnrollClient}
              ref={(c) => (this.enrollClientModal = c)}
            />
          </div>
        )}
      </Fragment>
    );
  }
}

ClientDetailsView.propTypes = propTypes;

export default withRouter(ClientDetailsView);
